import React from "react";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import {
  escapeSpecialRegexCharacters,
  vtRegexToRegex,
  isValidRegex,
  hexToRGBA,
  areTermsSame,
} from "utils/dictionary";
import { dictionaryDefaultColour } from "constants/dictionary";

if (process.env.NODE_ENV === "test") {
  const crypto = require("crypto");

  Object.defineProperty(globalThis, "crypto", {
    value: {
      getRandomValues: (arr) => crypto.randomBytes(arr.length),
    },
  });
}

export const getRegularExpressionGuidanceText = () => (
  <Modal.Body>
    <div className="edit_dictionary_page__wildcard_popup__more_note">
      <b>More about Regular Expressions</b>
    </div>
    <div className="edit_dictionary_page__wildcard_popup__characters_note">
      A regular expression (regex or regexp) uses a more mathematical syntax for
      pattern matching.
    </div>
    <div className="edit_dictionary_page__wildcard_popup__characters_note">
      Here is a simple example of a regex: [a-z]+ This regex matches any string
      that consists of one or more lowercase letters. For example, it would
      match the strings &quot;hello&quot;, &quot;world&quot;, and
      &quot;foobar&quot;.
    </div>
    <div className="edit_dictionary_page__wildcard_popup__characters_note">
      Here are some of the special characters that can be used in regular
      expressions:
    </div>
    <table className="edit_dictionary_page__wildcard_popup__table regular_expression_table">
      <thead>
        <tr>
          <th>Character</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <span>.</span>
          </td>
          <td>Matches any character.</td>
        </tr>
        <tr>
          <td>
            <span>[abc]</span>
          </td>
          <td>Matches any of the characters a, b, or c.</td>
        </tr>
        <tr>
          <td>
            <span>*</span>
          </td>
          <td>
            Matches zero or more occurrences of the previous character or group.
          </td>
        </tr>
        <tr>
          <td>
            <span>+</span>
          </td>
          <td>
            Matches one or more occurrences of the previous character or group.
          </td>
        </tr>
        <tr>
          <td>
            <span>?</span>
          </td>
          <td>
            Matches zero or one occurrence of the previous character or group.
          </td>
        </tr>
        <tr>
          <td>
            <span>\d</span>
          </td>
          <td>Matches a digit.</td>
        </tr>
        <tr>
          <td>
            <span>\s</span>
          </td>
          <td>Matches a whitespace character.</td>
        </tr>
        <tr>
          <td>
            <span>\w</span>
          </td>
          <td>Matches a word character (alphanumeric or underscore).</td>
        </tr>
      </tbody>
    </table>
    <div className="edit_dictionary_page__wildcard_popup__characters_note">
      You can also use parentheses to group together parts of a regex. For
      example, the regex (abc)+ matches one or more occurrences of the string
      &quot;abc&quot;.
    </div>
  </Modal.Body>
);

export const getWildcardExpressionGuidanceText = () => (
  <Modal.Body>
    <div className="edit_dictionary_page__wildcard_popup__more_note">
      <b>More about wildcards</b>
    </div>
    <div className="edit_dictionary_page__wildcard_popup__characters_note">
      Here are the special characters you can use
    </div>
    <table className="edit_dictionary_page__wildcard_popup__table wildcard_expression_table">
      <thead>
        <tr>
          <th>Character</th>
          <th>Description</th>
          <th>Example</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>*</td>
          <td>
            Matches any number of characters. You can use asterix (*) anywhere
            in the character string.
          </td>
          <td>
            <b>wh*</b> finds what, while and why, but not awhile or watch.
          </td>
        </tr>
        <tr>
          <td>?</td>
          <td>Matches a single alphabetic character in a specific position.</td>
          <td>
            <b>b?ll</b> finds ball, bell and bill.
          </td>
        </tr>
        <tr>
          <td>#</td>
          <td>Matches any single numeric character.</td>
          <td>
            <b>1#3</b> finds 113, 123, and 133 etc.
          </td>
        </tr>
        <tr>
          <td>#+</td>
          <td>Matches any number of numeric characters.</td>
          <td>
            <b>ISO#+</b> finds ISO900, ISO14001, but not ISO/TR19033.
          </td>
        </tr>
        <tr>
          <td>[]</td>
          <td>Matches any character inside the square brackets.</td>
          <td>
            <b>b[ae]ll</b> finds ball and bell, but not bill.
          </td>
        </tr>
        <tr>
          <td>-</td>
          <td>
            Matches a range of characters. Only used within square brackets.
          </td>
          <td>
            <b>b[a-c]</b> finds bad, bbd, and bcd.
          </td>
        </tr>
        <tr>
          <td>!</td>
          <td>Excludes characters inside the square brackets.</td>
          <td>
            <b>b[!ae]ll</b> does not find ball and bell. But it does find bill
            and bull.
            <br />
            <br />
            <b>[!a]*</b> finds any word not beginning with the letter a.
          </td>
        </tr>
      </tbody>
    </table>
  </Modal.Body>
);

const randomDecimal = () => {
  const randomBuffer = new Uint32Array(1);
  window.globalThis.crypto.getRandomValues(randomBuffer);
  return randomBuffer[0] / (0xffffffff + 1);
};

const randomNumber = (max) => Math.floor(randomDecimal() * max);

const nextBase = (base, digitBase) => (digitBase + 1) % base;

const nextPreviewCharacter = (base, code) =>
  String.fromCharCode(base.charCodeAt(0) + code);

const getPreviewLength = (
  previewLengths,
  segmentLength,
  previewLengthIndex
) => {
  if (previewLengthIndex === previewLengths.length) {
    previewLengths.push(randomNumber(segmentLength) + 2);
  }
  return previewLengths[previewLengthIndex];
};

const shuffleString = (s) =>
  s
    .split("")
    .sort(() => 0.5 - randomDecimal())
    .join("");

const printableASCIICharacterNotInString = (s) => {
  let t = "";
  for (let i = 33; i <= 123; i++) {
    const c = String.fromCharCode(i);
    if (
      c === "?" ||
      c === "$" ||
      c === "*" ||
      c === "(" ||
      c === "+" ||
      c === "&"
    ) {
      continue;
    }
    t += c;
  }
  t = shuffleString(t);
  for (let i = 0; i < t.length; i++) {
    if (s.indexOf(t[i]) === -1) {
      return t[i];
    }
  }
  return "";
};

export const generatePreview = (previewLengths, value, numberPreviews) => {
  let s = "";
  let i = 0;
  let characterBase = 0;
  let digitBase = 0;
  let previewLengthIndex = 0;
  const previews = [];
  for (let previewIndex = 0; previewIndex < numberPreviews; previewIndex++) {
    while (i < value.length) {
      switch (value[i]) {
        case "*":
          {
            const n = getPreviewLength(previewLengths, 3, previewLengthIndex);
            previewLengthIndex++;
            const c = nextPreviewCharacter("A", characterBase);
            characterBase = nextBase(26, characterBase);
            for (let j = 0; j < n; j++) {
              s += c;
            }
            i++;
          }
          break;
        case "?":
          s += nextPreviewCharacter("A", characterBase);
          characterBase = nextBase(26, characterBase);
          i++;
          break;
        case "#":
          digitBase = nextBase(10, digitBase);
          s += nextPreviewCharacter("0", digitBase);
          i++;
          break;
        case "+":
          if (i > 0 && value[i - 1] === "#") {
            const n = getPreviewLength(previewLengths, 3, previewLengthIndex);
            previewLengthIndex++;
            // digitBase = nextBase(10, digitBase)
            const c = nextPreviewCharacter("0", digitBase);
            for (let j = 0; j < n; j++) {
              s += c;
            }
          } else {
            s += value[i];
          }
          i++;
          break;
        case "[":
          {
            i++;
            let negate = false;
            let characters = "";
            let range = false;
            while (value[i] !== "]" && i < value.length) {
              if (value[i] === "!" && value[i - 1] === "[") {
                negate = true;
                i++;
              } else if (value[i] === "-" && value[i - 1] === "[") {
                characters += value[i];
                i++;
              } else if (value[i] === "-") {
                range = true;
                i++;
              } else {
                const from = characters[characters.length - 1];
                if (range && from < value[i]) {
                  for (
                    let c = from.charCodeAt(0) + 1;
                    c <= value[i].charCodeAt(0);
                    c++
                  ) {
                    characters += String.fromCharCode(c);
                  }
                  range = false;
                } else {
                  characters += value[i];
                }
                i++;
              }
            }
            i++; // bypass closing ]
            if (characters.length > 0) {
              s += negate
                ? printableASCIICharacterNotInString(shuffleString(characters))
                : shuffleString(characters)[0];
            }
          }
          break;
        default:
          s += value[i];
          i++;
          break;
      }
    }
    i = 0;
    if (previews.indexOf(s) === -1) {
      previews.push(s);
    }
    s = "";
  }
  return previews;
};

export const generatePreviewString = (
  previewLengths,
  value,
  numberPreviews
) => {
  return generatePreview(previewLengths, value, numberPreviews).join(", ");
};

export const isExpressionValid = (expert, expr) => {
  if (!expert) {
    expr = vtRegexToRegex(escapeSpecialRegexCharacters(expr));
  }
  return expr === "" || isValidRegex(expr);
};

export const renderPreview = (previewList, previewColour) => {
  const l = [];
  const backgroundColour =
    previewColour === dictionaryDefaultColour
      ? previewColour
      : hexToRGBA(previewColour, 0.2);

  previewList.forEach((p, i) => {
    l.push(
      <span
        style={{
          backgroundColor: backgroundColour,
          borderBottom: `2px solid ${previewColour}`,
        }}
      >
        {p}
      </span>
    );
    if (i !== previewList.length - 1) {
      l.push(", ");
    }
  });
  return l;
};

/* Term can't be same and have same colour as term from other category */
export const isColourValid = (term, categories) => {
  const { categoryId, colour } = term;

  if (colour === dictionaryDefaultColour) return true;

  for (let i = 0; i < categories.length; i++) {
    const category = categories[i];

    if (category.id === categoryId) continue;

    const { terms } = category;

    for (let j = 0; j < terms.length; j++) {
      const otherTerm = terms[j];

      if (colour !== otherTerm.colour) continue;

      if (areTermsSame(term, otherTerm)) return false;
    }
  }

  return true;
};
